<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('price.pricesLists') }}</span>
        <h3 class="page-title">
          <template v-if="$route.meta.type === 'products'">{{ $t('price.productsPricesList') }}</template>
          <template v-if="$route.meta.type === 'services'">{{ $t('price.servicesPricesList') }}</template>
          <template v-if="$route.meta.type === 'setups'">{{ $t('price.setupPricesList') }}</template>
        </h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          v-if="$route.meta.type === 'services' || $route.meta.type === 'products'"
          @click.prevent="openImportPopup"
          theme="primary"
          class="mr-2">
          <i class="material-icons">
            cloud_upload
          </i>
          {{ $t('price.importPrices') }}
        </d-button>

        <d-button
          v-if="$route.meta.type === 'services' || $route.meta.type === 'products'"
          @click.prevent="openExportPopup"
          theme="secondary"
          class="mr-2">
          <i class="material-icons">
            cloud_download
          </i>
          {{ $t('price.exportPrices') }}
        </d-button>

        <d-button
          v-if="$route.meta.type === 'products'"
          theme="success"
          @click.prevent="$router.push('/cenniki/produkty/nowy')">
            <i class="material-icons">add</i>
            {{ $t('price.addNewProductPrice') }}
          </d-button>
        <d-button
          v-if="$route.meta.type === 'services'"
          theme="success"
          @click.prevent="$router.push('/cenniki/zdobienia/nowy')">
            <i class="material-icons">add</i>
            {{ $t('price.addNewServicePrice') }}
          </d-button>
        <d-button
          v-if="$route.meta.type === 'setups'"
          theme="success"
          @click.prevent="$router.push('/cenniki/setup/nowy')">
            <i class="material-icons">add</i>
            {{ $t('price.addNewSetupPrice') }}
          </d-button>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="prices-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          :placeholder="$route.meta.type === 'products' ? $t('product.enterProductNameOrCode') : $t('service.enterServiceNameOrCode')"
          v-model="filters.search" />

        <v-select
          style="width: 240px;"
          class="ml-2"
          :options="currencyOptions"
          v-model="filters.currency"
          :placeholder="$t('settings.currency.currency')"
          :searchable="false"
          label="text">
        </v-select>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">
          {{ $t('ui.search') }}
        </d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">
          {{ $t('ui.resetFilters') }}
        </d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">
              <template v-if="$route.meta.type === 'products'">{{ $t('product.productCode') }}</template>
              <template v-if="$route.meta.type === 'services' || $route.meta.type === 'setups'">{{ $t('service.serviceCode') }}</template>
            </th>
            <th scope="col" class="border-0">{{ $t('ui.name') }}</th>
            <th scope="col" class="border-0">{{ $t('price.price') }}</th>
            <th scope="col" class="border-0">{{ $t('settings.currency.currency') }}</th>
            <th
              v-if="$route.meta.type === 'services'"
              scope="col"
              class="border-0">
              {{ $t('price.quantityFrom') }}
            </th>
            <th
              v-if="$route.meta.type === 'setups'"
              scope="col"
              class="border-0">
              {{ $t('price.quantityTo') }}
            </th>
            <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(price, index) of prices"
            :key="'country-' + index">
            <td class="align-middle">
              <template v-if="$route.meta.type === 'products'">
                <template v-if="price.product">
                  {{ price.product.code }}
                </template>
              </template>
              <template v-if="$route.meta.type === 'services' || $route.meta.type === 'setups'">
                <template v-if="price.service">
                  {{ price.service.code }}
                </template>
              </template>
            </td>
            <td class="align-middle">
              <template v-if="$route.meta.type === 'products'">
                <template v-if="price.product">
                  {{ price.product.name }}
                </template>
              </template>
              <template v-if="$route.meta.type === 'services' || $route.meta.type === 'setups'">
                <template v-if="price.service">
                  {{ price.service.name }}
                </template>
              </template>
            </td>
            <td class="align-middle">
              {{ Number(price.price).toFixed(2) }}
            </td>
            <td class="align-middle">
              <template v-if="price.currency">
                {{ price.currency.code }}
              </template>
            </td>
            <td
              v-if="$route.meta.type === 'services' || $route.meta.type === 'setups'"
              class="align-middle">
              {{ price.value_from }}
            </td>
            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push(editUrl + price.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removePrice(price.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="prices.length === 0">
            <td
              colspan="6"
              class="text-center">
              {{ $t('price.noPricesToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />

    <prices-export-popup
      v-if="showExportPopup"
      :currencies="currencies" />

    <prices-import-popup
      v-if="showImportPopup"
      :currencies="currencies" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import PricesExportPopup from './../components/prices/PricesExportPopup.vue';
import PricesImportPopup from './../components/prices/PricesImportPopup.vue';

export default {
  name: 'prices',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination,
    'prices-export-popup': PricesExportPopup,
    'prices-import-popup': PricesImportPopup
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedCurrencies;
    },
    currencyOptions () {
      return this.currencies.map(currency => ({
        value: currency.id,
        text: currency.code
      }));
    },
    editUrl () {
      if (this.$route.meta.type === 'products') {
        return '/cenniki/produkty/edycja/';
      }

      if (this.$route.meta.type === 'services') {
        return '/cenniki/zdobienia/edycja/';
      }

      if (this.$route.meta.type === 'setups') {
        return '/cenniki/setup/edycja/';
      }

      return '';
    },
    endpointBase () {
      if (this.$route.meta.type === 'products') {
        return '/customer-panel/products/customer-prices/';
      }

      if (this.$route.meta.type === 'services' || this.$route.meta.type === 'setups') {
        return '/customer-panel/services/customer-prices/';
      }

      return '';
    }
  },
  data() {
    return {
      prices: [],
      currencies: [],
      loaded: false,
      loadError: false,
      loadedCurrencies: false,
      showExportPopup: false,
      showImportPopup: false,
      removeInProgress: false,
      filters: {
        search: '',
        currency: ''
      }
    };
  },
  mounted () {
    this.loadFilteredData();
    this.loadAdditionalData();
    this.$bus.$on('prices-export-popup-close', this.closeExportPopup);
    this.$bus.$on('prices-import-popup-close', this.closeImportPopup);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search
      };

      if (this.filters.currency !== null && this.filters.currency && typeof this.filters.currency.value === 'number') {
        where.currency_id = this.filters.currency.value;
      }

      if (this.$route.meta.type === 'services') {
        where.is_setup = 0;
      }

      if (this.$route.meta.type === 'setups') {
        where.is_setup = 1;
      }

      ListUtils.loadPaginatedItemsData(this, {
        method: 'get',
        endpoint: this.endpointBase + 'items?page=' + this.currentPage,
        listField: 'prices',
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        currency: ''
      };

      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['prices-filters-form'], this.debouncedLoadFilteredData);
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/customer-panel/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });
    },
    removePrice (priceID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: this.endpointBase + 'delete/',
        id: priceID,
        successTitle: this.$t('price.deletePriceSuccessTitle'),
        successText: this.$t('price.deletePriceSuccessText'),
        confirmTitle: this.$t('price.deletePriceConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('price.deletePriceErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    openExportPopup () {
      this.showExportPopup = true;
    },
    closeExportPopup () {
      this.showExportPopup = false;
    },
    openImportPopup () {
      this.showImportPopup = true;
    },
    closeImportPopup () {
      this.showImportPopup = false;
    }
  },
  beforeDestroy () {
    this.$bus.$off('prices-export-popup-close', this.closeExportPopup);
    this.$bus.$off('prices-import-popup-close', this.closeImportPopup);
  }
};
</script>

